import { Link } from 'react-router-dom';
import { Phone, MapPin, Clock, ChevronRight, Brain, HeartPulse, Activity, Stethoscope, Users, FileText, Award, Star, BookOpen } from 'lucide-react';
import { Hero } from '../components/Hero';
import { AnimatedSection } from '../components/AnimatedSection';

const services = [
  {
    title: 'Traumatic Brain Injuries',
    description: 'Specialized care and rehabilitation for patients who have experienced traumatic brain injuries, focusing on recovery and functional improvement.',
    icon: Brain,
    href: '/services/traumatic-brain-injuries',
    image: 'https://images.unsplash.com/photo-1559757175-5700dde675bc?auto=format&fit=crop&q=80'
  },
  {
    title: 'Dysautonomia',
    description: 'Expert diagnosis and treatment of autonomic nervous system disorders, including POTS, orthostatic hypotension, and other autonomic dysfunctions.',
    icon: HeartPulse,
    href: '/services/dysautonomia',
    image: 'https://images.unsplash.com/photo-1631217868264-e5b90bb7e133?auto=format&fit=crop&q=80'
  },
  {
    title: 'Headaches',
    description: 'Comprehensive treatment for various types of headaches, including migraines, tension headaches, and cluster headaches.',
    icon: Activity,
    href: '/services/headaches',
    image: 'https://images.unsplash.com/photo-1541781774459-bb2af2f05b55?auto=format&fit=crop&q=80'
  },
  {
    title: 'Functional Neurologic Disorder',
    description: 'Specialized care for patients with functional neurologic disorders, addressing symptoms through an interdisciplinary rehabilitation approach.',
    icon: Stethoscope,
    href: '/services/fnd',
    image: 'https://images.unsplash.com/photo-1532094349884-543bc11b234d?auto=format&fit=crop&q=80'
  },
  {
    title: 'Neurodevelopmental Disorders',
    description: 'Evaluation and treatment of conditions affecting neurological development, with personalized strategies for both children and adults.',
    icon: Users,
    href: '/services/neurodevelopmental',
    image: 'https://images.unsplash.com/photo-1576091160399-112ba8d25d1d?auto=format&fit=crop&q=80'
  },
  {
    title: 'Movement Disorders',
    description: 'Expert diagnosis and treatment of conditions affecting movement, utilizing advanced therapies to improve motor function and patient independence.',
    icon: Activity,
    href: '/services/movement-disorders',
    image: 'https://images.unsplash.com/photo-1584515979956-d9f6e5d09982?auto=format&fit=crop&q=80'
  },
];

const highlights = [
  {
    icon: Award,
    title: '40+ Years Experience',
    description: 'Led by Dr. Jorge L. Juncos, former Director of Movement Disorders at Emory University'
  },
  {
    icon: Star,
    title: 'Specialized Expertise',
    description: 'Comprehensive care for complex neurological conditions using advanced treatment approaches'
  },
  {
    icon: BookOpen,
    title: 'Research Leadership',
    description: 'Contributing to neurological science through groundbreaking research and clinical trials'
  }
];

export function Home() {
  return (
    <div>
      <Hero
        title="Expert Neurological Care at Juncos Neuro Consulting"
        description="Dedicated to providing exceptional neurological care through innovative treatment approaches, groundbreaking research, and compassionate patient-centered care."
        image="https://images.unsplash.com/photo-1559757175-5700dde675bc?auto=format&fit=crop&q=80"
        size="large"
      >
        <div className="mt-8 flex gap-4">
          <Link
            to="/contact"
            className="inline-flex items-center rounded-md bg-white px-6 py-3 text-base font-semibold text-emory-blue shadow-md hover:shadow-lg hover:bg-emory-gray-100 transition-all transform hover:-translate-y-0.5"
          >
            Schedule an Appointment
            <ChevronRight className="ml-2 h-5 w-5" />
          </Link>
          <Link
            to="/about"
            className="inline-flex items-center rounded-md bg-emory-blue/10 backdrop-blur-sm px-6 py-3 text-base font-semibold text-white hover:bg-emory-blue/20 transition-all"
          >
            Learn More
            <ChevronRight className="ml-2 h-5 w-5" />
          </Link>
        </div>
      </Hero>

      {/* Quick Info Bar */}
      <div className="bg-white shadow-md transform -mt-8 relative z-10">
        <div className="mx-auto max-w-7xl px-4 py-6 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
            <AnimatedSection delay={0.1}>
              <div className="flex items-center gap-x-3 bg-gradient-cool p-4 rounded-lg shadow-glow">
                <Phone className="h-6 w-6 text-blue-600" />
                <div>
                  <p className="font-semibold">(470) 239-3602</p>
                  <p className="text-sm text-gray-600">Call us today</p>
                </div>
              </div>
            </AnimatedSection>
            <AnimatedSection delay={0.2}>
              <div className="flex items-center gap-x-3 bg-gradient-warm p-4 rounded-lg shadow-glow">
                <MapPin className="h-6 w-6 text-blue-600" />
                <div>
                  <p className="font-semibold">1100 Northside Forsyth Dr.</p>
                  <p className="text-sm text-gray-600">Suite 210, Cumming, GA 30041</p>
                </div>
              </div>
            </AnimatedSection>
            <AnimatedSection delay={0.3}>
              <div className="flex items-center gap-x-3 bg-gradient-healing p-4 rounded-lg shadow-glow">
                <Clock className="h-6 w-6 text-blue-600" />
                <div>
                  <p className="font-semibold">Mon–Thu: 9:00 AM – 5:00 PM</p>
                  <p className="text-sm text-gray-600">Fri: 9:00 AM – 12:00 PM</p>
                </div>
              </div>
            </AnimatedSection>
          </div>
        </div>
      </div>

      {/* Highlights Section */}
      <div className="py-16 bg-gradient-to-b from-white to-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900">Why Choose Juncos Neuro Consulting?</h2>
            <p className="mt-4 text-xl text-gray-600">
              Exceptional neurological care backed by decades of experience and research
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            {highlights.map((highlight, index) => {
              const Icon = highlight.icon;
              return (
                <AnimatedSection key={index} delay={index * 0.1}>
                  <div className="bg-white rounded-xl p-8 shadow-md hover:shadow-glow transition-all">
                    <div className="h-12 w-12 bg-gradient-cool rounded-lg flex items-center justify-center mb-6">
                      <Icon className="h-6 w-6 text-blue-600" />
                    </div>
                    <h3 className="text-xl font-bold text-gray-900 mb-3">{highlight.title}</h3>
                    <p className="text-gray-600">{highlight.description}</p>
                  </div>
                </AnimatedSection>
              );
            })}
          </div>
        </div>
      </div>

      {/* Services Overview */}
      <div className="py-16 bg-gradient-to-b from-gray-50 to-white">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900">Our Services</h2>
            <p className="mt-4 text-xl text-gray-600">
              Comprehensive neurological care tailored to your needs
            </p>
          </div>

          <div className="grid gap-8 sm:grid-cols-2 lg:grid-cols-3">
            {services.map((service, index) => {
              const Icon = service.icon;
              return (
                <AnimatedSection key={service.title} delay={index * 0.1}>
                  <Link
                    to={service.href}
                    className="group bg-white rounded-xl shadow-md hover:shadow-glow transition-all overflow-hidden"
                  >
                    <div className="h-48 overflow-hidden">
                      <img
                        src={service.image}
                        alt={service.title}
                        className="w-full h-full object-cover transform group-hover:scale-105 transition-transform duration-300"
                      />
                    </div>
                    <div className="p-6">
                      <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-gradient-cool mb-4">
                        <Icon className="h-6 w-6 text-blue-600" />
                      </div>
                      <h3 className="text-xl font-semibold text-gray-900 group-hover:text-blue-600 transition-colors mb-2">
                        {service.title}
                      </h3>
                      <p className="text-gray-600 mb-4">
                        {service.description}
                      </p>
                      <div className="flex items-center text-blue-600 font-medium">
                        Learn more
                        <FileText className="ml-2 h-4 w-4" />
                      </div>
                    </div>
                  </Link>
                </AnimatedSection>
              );
            })}
          </div>
        </div>
      </div>

      {/* CTA Section */}
      <div className="bg-gradient-to-r from-emory-blue to-emory-light-blue">
        <div className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-8">
          <div className="text-center">
            <AnimatedSection>
              <h2 className="text-3xl font-bold tracking-tight text-white sm:text-4xl">
                Ready to find answers for your neurological concerns?
              </h2>
              <p className="mx-auto mt-4 max-w-2xl text-lg text-blue-100">
                Contact us to schedule an appointment and take the first step towards better neurological health.
              </p>
              <div className="mt-8 flex justify-center gap-x-4">
                <Link
                  to="/contact"
                  className="rounded-md bg-white px-6 py-3 text-base font-semibold text-blue-700 shadow-sm hover:bg-blue-50 transition-all transform hover:-translate-y-0.5"
                >
                  Contact Us
                </Link>
                <Link
                  to="/patient-info/new-patients"
                  className="rounded-md border border-white bg-transparent px-6 py-3 text-base font-semibold text-white shadow-sm hover:bg-blue-800/20 transition-all"
                >
                  New Patient Info
                </Link>
              </div>
            </AnimatedSection>
          </div>
        </div>
      </div>
    </div>
  );
}